<template>
  <app-content>
    <page-title>{{ pageTitle }}</page-title>
    <main id="gallery">
      <fade-transition appear>
        <albums v-if="!currentAlbum" :albums="Object.values(albums)" :title="title"
                :loading="loading" @open="openAlbum"/>
        <album v-else :album="albums[currentAlbum]" :photos="photos[currentAlbum]"
               :loading="loading" @lightbox="openLightbox" @close="closeAlbum"/>
      </fade-transition>
    </main>
    <fade-transition>
      <div id="lightbox-wrapper" v-if="currentPhoto">
        <div id="lightbox-backdrop" @click="closeLightbox"></div>
        <div class="lightbox-header" @click="closeLightbox">
          <span class="lightbox-title">{{ albums[currentAlbum].name }}</span>
          <button class="lightbox-close" @click="closeLightbox" title="Schließen">
            <b-icon-x font-scale="2.75"/>
          </button>
        </div>
        <div class="lightbox-main">
          <div class="lightbox-content">
            <button class="lightbox-control" :class="{disabled: this.currentIndex < 1}"
                    @click="prevPhoto" title="Vorheriges Foto">
              <span><b-icon-chevron-left font-scale="2.75"/></span>
            </button>
            <fade-transition>
              <lightbox-photo :photo="photos[currentAlbum][currentPhoto]"/>
            </fade-transition>
            <button class="lightbox-control" title="Nächstes Foto"
                    :class="{disabled: this.currentIndex >= this.currentCount - 1}"
                    @click="nextPhoto">
              <span><b-icon-chevron-right font-scale="2.75"/></span>
            </button>
          </div>
        </div>
        <div class="lightbox-footer" @click="closeLightbox">
          {{ currentIndex + 1 }}/{{ currentCount }}
        </div>
      </div>
    </fade-transition>
    <fade-transition appear>
      <b-button v-if="upFab" variant="outline-light" class="floating-action-button back-up"
                v-b-tooltip.hover.topleft="'Zurück nach oben'" @click="toTop">
        <b-icon-chevron-bar-up/>
      </b-button>
    </fade-transition>
  </app-content>
</template>

<script>
import _ from 'lodash';

import AppContent from '@/components/technical/AppContent.vue';
import PageTitle from '@/components/PageTitle.vue';
import FadeTransition from '@/components/transition/FadeTransition.vue';

import LightboxPhoto from '@/components/gallery/LightboxPhoto.vue';

import Albums from '@/components/gallery/Albums.vue';
import Album from '@/components/gallery/Album.vue';

import WPlannerApi from '@/mixins/wplanner-api';
import Photo from '@/gallery/photo';


export default {
  name: 'Gallery',

  components: {
    AppContent,
    PageTitle,
    FadeTransition,
    LightboxPhoto,
    Albums,
    Album,
  },
  mixins: [WPlannerApi],

  data() {
    return {
      albums: {},
      photos: {},
      title: {},

      currentAlbum: null,
      currentPhoto: null,

      upFab: false,
      scrollHandler: null,
    };
  },

  computed: {
    pageTitle() {
      return !this.currentAlbum || !this.albums[this.currentAlbum] ? 'Galerie'
        : this.albums[this.currentAlbum].name;
    },

    fileIds() {
      const mapped = {};
      Object.entries(this.photos)
        .forEach(([albumId, photos]) => {
          mapped[albumId] = Object.keys(photos);
        });

      return mapped;
    },

    currentIndex() {
      return this.fileIds[this.currentAlbum].indexOf(this.currentPhoto);
    },

    currentCount() {
      return this.fileIds[this.currentAlbum].length;
    },
  },

  methods: {
    dateCompare(a, b) {
      if (a.getTime() < b.getTime()) {
        return -1;
      }
      if (a.getTime() > b.getTime()) {
        return 1;
      }

      return 0;
    },

    openAlbum(id) {
      this.currentPhoto = null;
      this.currentAlbum = id;

      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });

      this.loadPhotos(() => {});
    },

    closeAlbum() {
      this.currentAlbum = null;

      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    },

    openLightbox(id) {
      this.currentPhoto = id;
    },

    closeLightbox() {
      this.currentPhoto = null;
    },

    prevPhoto() {
      if (this.currentIndex > 0) {
        this.currentPhoto = this.fileIds[this.currentAlbum][this.currentIndex - 1];
      } else {
        this.currentPhoto = null;
      }
    },

    nextPhoto() {
      if (this.currentIndex < this.currentCount - 1) {
        this.currentPhoto = this.fileIds[this.currentAlbum][this.currentIndex + 1];
      } else {
        this.currentPhoto = null;
      }
    },

    async loadTitles(andThen) {
      if (Object.keys(this.title).length > 0) {
        andThen();
      } else {
        this.getAlbumTitles(this, Object.keys(this.albums), Photo.load,
          (titles) => {
            this.title = titles;
            andThen();
          });
      }
    },

    async loadPhotos(andThen) {
      if (this.currentAlbum && this.photos[this.currentAlbum]
        && Object.keys(this.photos[this.currentAlbum]).length > 0) {
        andThen();
      } else if (this.currentAlbum) {
        this.getAlbumFiles(this, this.currentAlbum, (photos) => {
          this.parsePhotos(photos.data);
          andThen();
        });
      } else if (Object.keys(this.photos) < 0) {
        this.getGalleryFiles(this, (photos) => {
          this.parsePhotos(photos.data);
          andThen();
        });
      } else {
        andThen();
      }
    },

    parsePhotos(data) {
      data.map(Photo.load)
        .forEach((photo) => {
          const newPhotos = { ...this.photos };
          newPhotos[photo.albumId] = { ...newPhotos[photo.albumId] };
          newPhotos[photo.albumId][photo.fileId] = photo;
          this.photos = newPhotos;
        });
    },

    async load() {
      this.getGalleryAlbums(this, (albumsResponse) => {
        this.albums = {};
        this.photos = {};
        this.title = {};

        albumsResponse.data
          // eslint-disable-next-line no-underscore-dangle
          .map(data => ({ id: data._id, name: data.name, date: new Date(data.date) }))
          .forEach((album) => {
            const newAlbums = { ...this.albums };
            newAlbums[album.id] = album;
            this.albums = newAlbums;
          });

        if (!this.currentAlbum) {
          this.loadTitles(() => this.loadPhotos(() => {
          }));
        } else {
          this.loadPhotos(() => this.loadTitles(() => {
          }));
        }
      });
    },

    handleScroll() {
      // eslint-disable-next-line no-console
      console.log(window.scrollY);
      this.upFab = window.scrollY > 50;
    },

    toTop() {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    },
  },

  mounted() {
    this.load();

    this.scrollHandler = _.debounce(this.handleScroll, 250);
    window.addEventListener('scroll', this.scrollHandler);
    window.setTimeout(this.scrollHandler, 250);
  },

  beforeDestroy() {
    if (this.scrollHandler != null) window.removeEventListener('scroll', this.scrollHandler);
  },
};
</script>

<style lang="scss">
.floating-action-button {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  padding: 0.35rem;

  box-shadow: 0 0 6px rgba(0, 0, 0, 0.3);

  line-height: 1;

  animation-fill-mode: both;

  transform: translateZ(0);
  backface-visibility: hidden;
}

$fab-color: #c6c6c6;

.back-up {
  font-size: 2rem;

  color: $fab-color;
  border-color: $fab-color;
  background-color: $body-bg;

  transition-property: color, background-color, box-shadow;
  transition-duration: 500ms;
  transition-timing-function: ease;

  &:hover {
    color: $body-bg;
    background-color: $fab-color;
    border-color: $body-bg;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
  }
}
</style>
